/**
 * Featured Reviews Carousel Component
 * Manages a carousel of customer reviews with images
 * Supports keyboard navigation, autoplay, and respects user motion preferences
 */
class FeaturedReviews extends HTMLElement {
  constructor() {
    super();

    // DOM element references
    this.refs = {
      slides: null,
      images: null,
      nextButton: null,
      prevButton: null,
      liveRegion: null
    };

    // State
    this.state = {
      current: 0,
      total: 0,
      isAutoplayEnabled: false,
      autoplayInterval: null,
      isPaused: false
    };

    // Configuration
    this.config = {
      autoplayDelay: 5000,
      transitionDuration: 600,
      prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
    };

    // Bind methods to preserve context
    this.handleNext = this.handleNext.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback() {
    // Initialize DOM references
    this.initializeRefs();

    // Validate required elements exist
    if (!this.validateRefs()) {
      return;
    }

    // Initialize state
    this.state.total = this.refs.slides.length;

    // Don't initialize if no slides or only one slide
    if (this.state.total <= 1) {
      return;
    }

    // Setup accessibility
    this.setupAccessibility();

    // Setup event listeners
    this.setupEventListeners();

    // Show navigation buttons
    this.showNavigationButtons();

    // Update button states
    this.updateButtonStates();

    // Initialize first slide
    this.showSlide(0);

    // Start autoplay if enabled
    this.initializeAutoplay();
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    this.removeEventListeners();

    // Stop autoplay
    this.stopAutoplay();
  }

  /**
   * Initialize DOM element references
   */
  initializeRefs() {
    this.refs.slides = Array.from(this.querySelectorAll('.featured-reviews__slide'));
    this.refs.images = Array.from(this.querySelectorAll('.featured-reviews__image'));
    this.refs.nextButton = this.querySelector('.prev-next-arrow-button__arrow--next');
    this.refs.prevButton = this.querySelector('.prev-next-arrow-button__arrow--prev');

    // Create live region for screen reader announcements
    this.refs.liveRegion = this.createLiveRegion();
  }

  /**
   * Validate that required DOM elements exist
   * @returns {boolean} True if all required elements exist
   */
  validateRefs() {
    if (!this.refs.slides || this.refs.slides.length === 0) {
      console.error('FeaturedReviews: No slides found');
      return false;
    }

    if (!this.refs.images || this.refs.images.length === 0) {
      console.error('FeaturedReviews: No images found');
      return false;
    }

    if (this.refs.slides.length !== this.refs.images.length) {
      console.error('FeaturedReviews: Slide and image count mismatch');
      return false;
    }

    return true;
  }

  /**
   * Create ARIA live region for screen reader announcements
   * @returns {HTMLElement} The live region element
   */
  createLiveRegion() {
    const existing = this.querySelector('.featured-reviews__sr-live-region');
    if (existing) {
      return existing;
    }

    const liveRegion = document.createElement('div');
    liveRegion.className = 'featured-reviews__sr-live-region visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    this.appendChild(liveRegion);
    return liveRegion;
  }

  /**
   * Setup accessibility attributes
   */
  setupAccessibility() {
    // Add region role and label to carousel container
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Customer reviews carousel');

    // Add roledescription and labels to slides
    this.refs.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Review ${index + 1} of ${this.state.total}`);
    });

    // Add button labels if not present
    if (this.refs.nextButton && !this.refs.nextButton.getAttribute('aria-label')) {
      this.refs.nextButton.setAttribute('aria-label', 'Next review');
    }

    if (this.refs.prevButton && !this.refs.prevButton.getAttribute('aria-label')) {
      this.refs.prevButton.setAttribute('aria-label', 'Previous review');
    }
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Navigation buttons
    if (this.refs.nextButton) {
      this.refs.nextButton.addEventListener('click', this.handleNext);
    }

    if (this.refs.prevButton) {
      this.refs.prevButton.addEventListener('click', this.handlePrevious);
    }

    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeydown);

    // Pause autoplay on hover/focus
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleMouseEnter);
    this.addEventListener('focusout', this.handleMouseLeave);

    // Recalculate font sizes on window resize
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners() {
    if (this.refs.nextButton) {
      this.refs.nextButton.removeEventListener('click', this.handleNext);
    }

    if (this.refs.prevButton) {
      this.refs.prevButton.removeEventListener('click', this.handlePrevious);
    }

    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleMouseEnter);
    this.removeEventListener('focusout', this.handleMouseLeave);

    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Show navigation buttons
   */
  showNavigationButtons() {
    if (this.refs.nextButton) {
      this.refs.nextButton.classList.add('mut-featured-reviews__arrow--show');
    }

    if (this.refs.prevButton) {
      this.refs.prevButton.classList.add('mut-featured-reviews__arrow--show');
    }
  }

  /**
   * Handle next button click
   */
  handleNext() {
    this.navigateToSlide((this.state.current + 1) % this.state.total);
  }

  /**
   * Handle previous button click
   */
  handlePrevious() {
    this.navigateToSlide((this.state.current - 1 + this.state.total) % this.state.total);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    // Only handle if focus is within component or on navigation buttons
    if (!this.contains(document.activeElement)) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.handlePrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.handleNext();
        break;
      case 'Home':
        event.preventDefault();
        this.navigateToSlide(0);
        break;
      case 'End':
        event.preventDefault();
        this.navigateToSlide(this.state.total - 1);
        break;
    }
  }

  /**
   * Handle mouse enter or focus in - pause autoplay
   */
  handleMouseEnter() {
    if (this.state.isAutoplayEnabled) {
      this.pauseAutoplay();
    }
  }

  /**
   * Handle mouse leave or focus out - resume autoplay
   */
  handleMouseLeave() {
    if (this.state.isAutoplayEnabled && this.state.isPaused) {
      this.resumeAutoplay();
    }
  }

  /**
   * Handle window resize - recalculate font sizes
   */
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const currentSlide = this.refs.slides[this.state.current];
      if (currentSlide) {
        this.adjustReviewFontSize(currentSlide);
      }
    }, 150);
  }

  /**
   * Navigate to a specific slide
   * @param {number} index - The slide index to navigate to
   */
  navigateToSlide(index) {
    if (index === this.state.current) {
      return;
    }

    const previousIndex = this.state.current;
    this.state.current = index;

    // Hide previous slide
    this.hideSlide(previousIndex);

    // Show new slide
    this.showSlide(index);

    // Update button states
    this.updateButtonStates();

    // Announce to screen readers
    this.announceSlideChange(index);
  }

  /**
   * Show a specific slide
   * @param {number} index - The slide index to show
   */
  showSlide(index) {
    const slide = this.refs.slides[index];
    const image = this.refs.images[index];

    if (!slide || !image) {
      return;
    }

    // Add show class
    slide.classList.add('mut-featured-reviews--show');
    image.classList.add('mut-featured-reviews--show');

    // Update aria-current
    slide.setAttribute('aria-current', 'true');

    // Adjust font size based on review length
    this.adjustReviewFontSize(slide);

    // Preload next image for smooth transition
    this.preloadAdjacentImages(index);
  }

  /**
   * Hide a specific slide
   * @param {number} index - The slide index to hide
   */
  hideSlide(index) {
    const slide = this.refs.slides[index];
    const image = this.refs.images[index];

    if (!slide || !image) {
      return;
    }

    // Remove show class
    slide.classList.remove('mut-featured-reviews--show');
    image.classList.remove('mut-featured-reviews--show');

    // Remove aria-current
    slide.removeAttribute('aria-current');
  }

  /**
   * Update button disabled states
   * Note: For circular navigation, buttons are never disabled
   * This method can be customized if linear navigation is desired
   */
  updateButtonStates() {
    // For circular carousel, buttons are always enabled
    if (this.refs.nextButton) {
      this.refs.nextButton.removeAttribute('aria-disabled');
      this.refs.nextButton.disabled = false;
    }

    if (this.refs.prevButton) {
      this.refs.prevButton.removeAttribute('aria-disabled');
      this.refs.prevButton.disabled = false;
    }
  }

  /**
   * Announce slide change to screen readers
   * @param {number} index - The current slide index
   */
  announceSlideChange(index) {
    if (!this.refs.liveRegion) {
      return;
    }

    const announcement = `Showing review ${index + 1} of ${this.state.total}`;
    this.refs.liveRegion.textContent = announcement;
  }

  /**
   * Adjust review font size based on content length to fill available space
   * Short reviews get larger text, long reviews get smaller text (min 16px)
   * @param {HTMLElement} slide - The slide element containing the review
   */
  adjustReviewFontSize(slide) {
    const reviewElement = slide.querySelector('.mut-featured-reviews__review');
    if (!reviewElement) {
      return;
    }

    // Wait for next frame to ensure layout is complete
    requestAnimationFrame(() => {
      const reviewText = reviewElement.textContent || '';
      const reviewLength = reviewText.trim().length;

      // Calculate font size based on text length
      // Short reviews (< 100 chars): 40px
      // Medium reviews (100-300 chars): 32-36px
      // Long reviews (300-500 chars): 24-28px
      // Very long reviews (> 500 chars): 18-20px (min 18px)

      let fontSize;

      if (reviewLength < 100) {
        // Very short reviews: large text
        fontSize = 40;
      } else if (reviewLength < 200) {
        // Short reviews: interpolate between 40px and 34px
        fontSize = 40 - ((reviewLength - 100) / 100) * 6;
      } else if (reviewLength < 350) {
        // Medium reviews: interpolate between 34px and 26px
        fontSize = 34 - ((reviewLength - 200) / 150) * 8;
      } else if (reviewLength < 550) {
        // Long reviews: interpolate between 26px and 20px
        fontSize = 26 - ((reviewLength - 350) / 200) * 6;
      } else {
        // Very long reviews: minimum size 18px
        fontSize = Math.max(18, 20 - ((reviewLength - 550) / 250) * 2);
      }

      // Apply the calculated font size
      reviewElement.style.fontSize = `${Math.round(fontSize)}px`;
      reviewElement.style.lineHeight = '1.3';
    });
  }

  /**
   * Preload adjacent images for smoother transitions
   * @param {number} currentIndex - The current slide index
   */
  preloadAdjacentImages(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.state.total;
    const prevIndex = (currentIndex - 1 + this.state.total) % this.state.total;

    [nextIndex, prevIndex].forEach(index => {
      const image = this.refs.images[index];
      if (!image) {
        return;
      }

      const img = image.querySelector('img');
      if (img && !img.complete) {
        // Image exists but not loaded, browser will prioritize loading it
        img.loading = 'eager';
      }
    });
  }

  /**
   * Initialize autoplay from data attributes
   */
  initializeAutoplay() {
    const autoplayEnabled = this.dataset.autoplay === 'true';
    const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 10000;

    if (autoplayEnabled && !this.config.prefersReducedMotion) {
      this.startAutoplay(autoplayDelay);
    }
  }

  /**
   * Start autoplay
   * @param {number} delay - Delay in milliseconds between slides
   */
  startAutoplay(delay = this.config.autoplayDelay) {
    if (this.state.isAutoplayEnabled || this.config.prefersReducedMotion) {
      return;
    }

    this.state.isAutoplayEnabled = true;
    this.state.isPaused = false;

    this.state.autoplayInterval = setInterval(() => {
      if (!this.state.isPaused) {
        this.handleNext();
      }
    }, delay);
  }

  /**
   * Stop autoplay
   */
  stopAutoplay() {
    if (this.state.autoplayInterval) {
      clearInterval(this.state.autoplayInterval);
      this.state.autoplayInterval = null;
    }

    this.state.isAutoplayEnabled = false;
    this.state.isPaused = false;
  }

  /**
   * Pause autoplay (keeps it enabled but stops transitions)
   */
  pauseAutoplay() {
    this.state.isPaused = true;
  }

  /**
   * Resume autoplay
   */
  resumeAutoplay() {
    this.state.isPaused = false;
  }
}

// Register custom element when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('featured-reviews', FeaturedReviews);
  });
} else {
  customElements.define('featured-reviews', FeaturedReviews);
}
